import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Download, EditIcon, RotateCcw } from "@/components/icons";
import { Button, Loader, toast, confirm } from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { ProductFormPageHeader } from "@/modules/products/presentation/ui/ProductFormPageHeader";
import {
  useCancelImportOrder,
  useImportOrder,
  useMarkImportOrderOrdered,
  useMarkImportOrderShort,
  useMatchImportOrderLine,
  useReceiveImportOrder,
  useReverseLegacyImportOrderReceive,
  useSkipImportOrderLine,
  useVoidImportOrderGoodsReceipt,
  useVoidImportOrderReceive,
} from "../../api/import-orders-queries";
import { RECEIVABLE_IMPORT_STATUSES } from "../../lib/import-order-labels";
import {
  ImportOrderDetailActionsBar,
  ImportOrderDetailFinancePanel,
  ImportOrderDetailGoodsReceiptsPanel,
  ImportOrderDetailHero,
  ImportOrderDetailLegacyReceiptsPanel,
  ImportOrderDetailTimelinePanel,
  ImportOrderDetailTrackingsPanel,
} from "../../ui/ImportOrderDetailSections";
import { ImportOrderLinesPanel } from "../../ui/ImportOrderLinesPanel";
import { ImportOrderMatchDialog } from "../../ui/ImportOrderMatchDialog";
import { MarkImportOrderShortDialog } from "../../ui/MarkImportOrderShortDialog";
import { ReceiveImportOrderDialog } from "../../ui/ReceiveImportOrderDialog";
import type { ImportOrderPhase13LineDTO } from "@/modules/inventory/domain/types";

export function ImportOrderDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/import-orders/$id" });
  const order = useImportOrder(id);
  const markOrdered = useMarkImportOrderOrdered();
  const receiveOrder = useReceiveImportOrder();
  const voidReceive = useVoidImportOrderReceive();
  const voidGoodsReceipt = useVoidImportOrderGoodsReceipt();
  const reverseLegacy = useReverseLegacyImportOrderReceive();
  const markShort = useMarkImportOrderShort();
  const cancelOrder = useCancelImportOrder();
  const matchLine = useMatchImportOrderLine();
  const skipLine = useSkipImportOrderLine();

  const canWrite = useActionPermission("import_orders:write");
  const canReceive = useActionPermission("import_orders:receive");
  const canCancel = useActionPermission("import_orders:cancel");

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [shortOpen, setShortOpen] = useState(false);
  const [matchLineTarget, setMatchLineTarget] =
    useState<ImportOrderPhase13LineDTO | null>(null);

  const data = order.data;
  const isPending =
    markOrdered.isPending ||
    receiveOrder.isPending ||
    voidReceive.isPending ||
    voidGoodsReceipt.isPending ||
    reverseLegacy.isPending ||
    markShort.isPending ||
    cancelOrder.isPending ||
    matchLine.isPending ||
    skipLine.isPending;

  if (order.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex justify-center py-16">
          <Loader />
        </Main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8 text-center text-muted-foreground text-sm">
            ບໍ່ພົບໃບສັ່ງນຳເຂົ້າ
          </div>
        </Main>
      </>
    );
  }

  const canReceiveNow =
    canReceive &&
    RECEIVABLE_IMPORT_STATUSES.has(data.status) &&
    data.totalRemaining > 0;

  const confirmedReceiptCount = (data.receipts ?? []).filter(
    (r) => r.status === "confirmed",
  ).length;
  const activeGoodsReceipts = (data.goodsReceipts ?? []).filter(
    (r) => r.status !== "void",
  );
  const confirmedGoodsReceipts = activeGoodsReceipts.filter(
    (r) => r.status === "confirmed",
  );
  const latestConfirmedGoodsReceipt = confirmedGoodsReceipts[0] ?? null;
  const hasLinkedGoodsReceipts = activeGoodsReceipts.length > 0;
  const canReverseLegacy =
    canReceive &&
    data.totalReceived > 0 &&
    confirmedReceiptCount === 0 &&
    !hasLinkedGoodsReceipts;
  const canReverseLatestReceive =
    canReceive &&
    (data.status === "received" || data.status === "partially_received") &&
    latestConfirmedGoodsReceipt != null;

  const showPrimaryActions =
    canWrite && data.status !== "cancelled" && data.status !== "received";
  const showReverseActions = canReverseLatestReceive || canReverseLegacy;

  async function runAction(label: string, action: () => Promise<unknown>) {
    try {
      await action();
      toast.success(label);
    } catch (e) {
      toast.error("ດຳເນີນການລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  async function handleVoidReceive(receiptId: string) {
    const ok = await confirm({
      title: "ຍ້ອນການຮັບເຂົ້າ?",
      description:
        "ຈະຫັກສະຕັອກຄືນ ແລະ ປ່ຽນສະຖານະໃບສັ່ງຕາມຈຳນວນທີ່ຍັງຮັບຄົງເຫຼືອ — ເຮັດໄດ້ເມື່ອສິນຄ້າຍັງບໍ່ຖືກຂາຍ",
      actionText: "ຍ້ອນການຮັບເຂົ້າ",
      cancelText: "ຍົກເລີກ",
      tone: "destructive",
    });
    if (!ok) return;
    await runAction("ຍ້ອນການຮັບເຂົ້າແລ້ວ", () =>
      voidReceive.mutateAsync({ id, receiptId }),
    );
  }

  async function handleVoidGoodsReceipt(goodsReceiptId: string) {
    const ok = await confirm({
      title: "ຍ້ອນສະຖານະຮັບແລ້ວ?",
      description:
        "ຈະຫັກສະຕັອກຄືນ ແລະ ປ່ຽນສະຖານະໃບສັ່ງກັບຄືນ — ເຮັດໄດ້ເມື່ອສິນຄ້າຍັງບໍ່ຖືກຂາຍ ຫຼື ຈອງ",
      actionText: "ຍ້ອນສະຖານະຮັບແລ້ວ",
      cancelText: "ຍົກເລີກ",
      tone: "destructive",
    });
    if (!ok) return;
    await runAction("ຍ້ອນສະຖານະຮັບແລ້ວສຳເລັດ", () =>
      voidGoodsReceipt.mutateAsync({ id, goodsReceiptId }),
    );
  }

  async function handleReverseLegacy() {
    const ok = await confirm({
      title: "ຍ້ອນສະຖານະຮັບເຂົ້າ?",
      description:
        "ໃບສັ່ງເກົ່າບໍ່ມີປະຫວັດຮັບເຂົ້າ — ລະບົບຈະຣີເຊັດຈຳນວນຮັບ ແລະ ຫັກສະຕັອກຕາມທີ່ຍັງຄົງເຫຼືອ",
      actionText: "ຍ້ອນສະຖານະຮັບເຂົ້າ",
      cancelText: "ຍົກເລີກ",
      tone: "destructive",
    });
    if (!ok) return;
    await runAction("ຍ້ອນສະຖານະຮັບເຂົ້າແລ້ວ", () => reverseLegacy.mutateAsync(id));
  }

  async function handleSkipLine(lineId: string) {
    const ok = await confirm({
      title: "ຂ້າມລາຍການ?",
      description:
        "ລາຍການທີ່ຍັງບໍ່ຈັບຄູ່ SKU ຈະບໍ່ຖືກຮັບເຂົ້າ — ສາມາດເບິ່ງປະຫວັດໄດ້ໃນລາຍລະອຽດ",
      actionText: "ຂ້າມລາຍການ",
      cancelText: "ຍົກເລີກ",
      tone: "destructive",
    });
    if (!ok) return;
    await runAction("ຂ້າມລາຍການແລ້ວ", () =>
      skipLine.mutateAsync({ importOrderId: id, lineId, body: {} }),
    );
  }

  return (
    <>
      <Header />
      <Main className="flex min-w-0 flex-col gap-4 overflow-x-hidden">
        <ProductFormPageHeader
          title="ລາຍລະອຽດໃບສັ່ງນຳເຂົ້າ"
          subtitle={data.orderNumber}
          onBack={() => nav({ to: "/app/import-orders" })}
        />

        <ImportOrderDetailHero order={data} />

        {showPrimaryActions || showReverseActions ? (
          <ImportOrderDetailActionsBar>
            {showPrimaryActions ? (
              <>
                {data.status === "draft" ? (
                  <Button
                    variant="outline"
                    className="rounded-full"
                    disabled={isPending}
                    onClick={() =>
                      runAction("ຢືນຢັນສັ່ງແລ້ວ", () =>
                        markOrdered.mutateAsync(id),
                      )
                    }
                  >
                    ຢືນຢັນສັ່ງແລ້ວ
                  </Button>
                ) : null}
                {canReceiveNow ? (
                  <Button
                    className="rounded-full"
                    disabled={isPending}
                    onClick={() => setReceiveOpen(true)}
                  >
                    <Download data-icon="inline-start" />
                    ສ້າງໃບຮັບເຂົ້າ ({data.totalRemaining} ຄ້າງ)
                  </Button>
                ) : null}
                {canReceiveNow ? (
                  <Button
                    variant="outline"
                    className="rounded-full"
                    disabled={isPending}
                    onClick={() => setShortOpen(true)}
                  >
                    ບັນທຶກຂອງຂາດ
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  className="rounded-full"
                  onClick={() =>
                    nav({ to: "/app/import-orders/$id/edit", params: { id } })
                  }
                >
                  <EditIcon data-icon="inline-start" />
                  ແກ້ໄຂ
                </Button>
                {canCancel ? (
                  <Button
                    variant="ghost"
                    className="rounded-full text-destructive"
                    disabled={isPending}
                    onClick={() =>
                      runAction("ຍົກເລີກໃບສັ່ງ", () =>
                        cancelOrder.mutateAsync(id),
                      )
                    }
                  >
                    ຍົກເລີກ
                  </Button>
                ) : null}
              </>
            ) : null}
            {canReverseLatestReceive && latestConfirmedGoodsReceipt ? (
              <Button
                variant="outline"
                className="rounded-full border-destructive/30 text-destructive"
                disabled={isPending}
                onClick={() =>
                  handleVoidGoodsReceipt(latestConfirmedGoodsReceipt.id)
                }
              >
                <RotateCcw data-icon="inline-start" />
                ຍ້ອນສະຖານະຮັບແລ້ວ
              </Button>
            ) : null}
            {canReverseLegacy ? (
              <Button
                variant="outline"
                className="rounded-full border-destructive/30 text-destructive"
                disabled={isPending}
                onClick={() => handleReverseLegacy()}
              >
                <RotateCcw data-icon="inline-start" />
                ຍ້ອນສະຖານະຮັບເຂົ້າ ({data.totalReceived})
              </Button>
            ) : null}
          </ImportOrderDetailActionsBar>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <ImportOrderLinesPanel
              lines={data.lines}
              currency={data.currency}
              canMatch={
                !!canWrite &&
                data.status !== "cancelled" &&
                data.status !== "received"
              }
              onMatchLine={(lineId) => {
                const line = data.lines.find((item) => item.id === lineId) ?? null;
                setMatchLineTarget(line);
              }}
              onSkipLine={handleSkipLine}
            />
            <ImportOrderDetailTrackingsPanel trackings={data.trackings} />
            <ImportOrderDetailGoodsReceiptsPanel
              receipts={data.goodsReceipts ?? []}
              canReceive={!!canReceive}
              isPending={isPending}
              onOpen={(receiptId) =>
                nav({
                  to: "/app/inventory/receive/$id",
                  params: { id: receiptId },
                })
              }
              onVoid={handleVoidGoodsReceipt}
            />
            <ImportOrderDetailLegacyReceiptsPanel
              receipts={data.receipts ?? []}
              canReceive={!!canReceive}
              isPending={isPending}
              onVoid={handleVoidReceive}
            />
          </div>

          <div className="flex flex-col gap-4">
            <ImportOrderDetailFinancePanel order={data} />
            <ImportOrderDetailTimelinePanel order={data} />
          </div>
        </div>
      </Main>

      <ReceiveImportOrderDialog
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        order={data}
        isSubmitting={receiveOrder.isPending}
        onConfirm={async (body) => {
          try {
            const result = await receiveOrder.mutateAsync({ id, body });
            setReceiveOpen(false);
            toast.success("ສ້າງໃບຮັບເຂົ້າແລ້ວ — ກະລຸນາຢືນຢັນເພື່ອເພີ່ມສະຕັອກ");
            if (result.goodsReceipt?.id) {
              nav({
                to: "/app/inventory/receive/$id",
                params: { id: result.goodsReceipt.id },
              });
            }
          } catch (e) {
            toast.error("ດຳເນີນການລົ້ມເຫຼວ", {
              description: e instanceof Error ? e.message : undefined,
            });
          }
        }}
      />

      <MarkImportOrderShortDialog
        open={shortOpen}
        onOpenChange={setShortOpen}
        lines={data.lines}
        isSubmitting={markShort.isPending}
        onConfirm={async (body) => {
          await runAction("ບັນທຶກຂອງຂາດແລ້ວ", () =>
            markShort.mutateAsync({ id, body }),
          );
          setShortOpen(false);
        }}
      />

      <ImportOrderMatchDialog
        open={matchLineTarget != null}
        onOpenChange={(open) => {
          if (!open) setMatchLineTarget(null);
        }}
        line={matchLineTarget}
        currency={data.currency}
        isSubmitting={matchLine.isPending}
        onConfirm={async (body) => {
          if (!matchLineTarget) return;
          try {
            await matchLine.mutateAsync({
              importOrderId: id,
              lineId: matchLineTarget.id,
              body,
            });
            setMatchLineTarget(null);
            toast.success("ຈັບຄູ່ SKU ແລ້ວ");
          } catch (e) {
            toast.error("ຈັບຄູ່ລົ້ມເຫຼວ", {
              description: e instanceof Error ? e.message : undefined,
            });
          }
        }}
      />
    </>
  );
}
